/**
 * Workflow origin (ALC-4564, D34): where a rule came from.
 *
 * - `DESCRIPTION`: the person described the rule in words and an AI drafted it. `text` is what the
 *   person wrote, kept verbatim as the record of why the rule exists.
 * - `APPROVAL_OFFER`: the rule was offered while a bill was being approved (ALC-4565). The person
 *   wrote nothing, so there is no text: the source bill is the evidence.
 *
 * A hand-made rule and a rule made through MCP have no origin (`null`). The origin is set once, at
 * create, and never changes: the database refuses an UPDATE of the three columns
 * (`wf_workflow_origin_immutable`) and the API refuses a PATCH that carries one.
 *
 * Provenance, not authority: the caller declares it (owner question 1), so nothing may gate an
 * action on it. The audit trail (ALC-4644) records it with the actor.
 *
 * `checkWorkflowOriginInput` is the same rule as the CHECK `wf_workflow_origin_shape_chk`, plus the
 * 2000 character limit and the allowed triggers, which only the API enforces. A DESCRIPTION origin
 * is allowed only on `bill_updated`: a description drafts a "send to approval" rule, and the
 * approval-start trigger can run `auto_approve`, so a rule there must not claim to come from one
 * (r1, accounting 0). An APPROVAL_OFFER keeps both bill triggers (ALC-4565 writes one of each). It runs BEFORE the
 * database so a bad shape answers with a stable code, not a constraint error.
 */

import { WorkflowOriginKind } from './generated/enums.js';
import { WorkflowTriggerTypeCode } from './wf.js';

/** The longest `text` an origin keeps, after trimming. The database has no bound (the API owns it). */
export const WORKFLOW_ORIGIN_TEXT_MAX_LENGTH = 2000;

/** The triggers a rule with an origin can have. Both are bill rules. */
export const WORKFLOW_ORIGIN_TRIGGER_CODES: readonly string[] = [
  WorkflowTriggerTypeCode.BILL_UPDATED,
  WorkflowTriggerTypeCode.BILL_APPROVAL_WORKFLOW_INIT,
];

/** The triggers a DESCRIPTION origin can have: only the rule a description drafts. */
export const WORKFLOW_DESCRIPTION_ORIGIN_TRIGGER_CODES: readonly string[] = [
  WorkflowTriggerTypeCode.BILL_UPDATED,
];

/** What a caller sends on create. Unknown keys are dropped; `createdBy` and the date are the row's. */
export interface WorkflowOriginInput {
  kind: WorkflowOriginKind;
  /** DESCRIPTION: required. APPROVAL_OFFER: must be absent or null. */
  text?: string | null;
  /** APPROVAL_OFFER: required. DESCRIPTION: optional. */
  sourceBillId?: string | null;
}

/** What a workflow response shows. `createdBy` and `createdAt` are the rule's own, never separate columns. */
export interface WorkflowOriginResponse {
  kind: WorkflowOriginKind;
  text: string | null;
  sourceBillId: string | null;
  createdBy: string;
  createdAt: string;
}

export const WorkflowOriginErrorCode = {
  /** The origin shape is wrong. The body also carries a `reason` (see {@link WorkflowOriginInvalidReason}). */
  INVALID: 'WORKFLOW_ORIGIN_INVALID',
  /** A PATCH carried an origin. It is set at create and never changes. */
  IMMUTABLE: 'WORKFLOW_ORIGIN_IMMUTABLE',
  /** An origin came through an MCP or other API token. Only a person at the screen declares one. */
  CHANNEL_NOT_ALLOWED: 'WORKFLOW_ORIGIN_CHANNEL_NOT_ALLOWED',
  /** The source bill does not exist for this caller. A bill outside the caller's scope answers the same. */
  SOURCE_BILL_NOT_FOUND: 'WORKFLOW_ORIGIN_SOURCE_BILL_NOT_FOUND',
} as const;
export type WorkflowOriginErrorCode =
  (typeof WorkflowOriginErrorCode)[keyof typeof WorkflowOriginErrorCode];

export interface WorkflowOriginCopy {
  ptBr: string;
  en: string;
}

export const WORKFLOW_ORIGIN_ERROR_MESSAGES: Record<WorkflowOriginErrorCode, WorkflowOriginCopy> = {
  [WorkflowOriginErrorCode.INVALID]: {
    ptBr: 'A origem da regra não é válida.',
    en: 'The origin of the rule is not valid.',
  },
  [WorkflowOriginErrorCode.IMMUTABLE]: {
    ptBr: 'A origem de uma regra é definida ao criá-la e não pode ser alterada.',
    en: 'The origin of a rule is set when it is created and cannot be changed.',
  },
  [WorkflowOriginErrorCode.CHANNEL_NOT_ALLOWED]: {
    ptBr: 'Somente uma pessoa na tela pode informar a origem de uma regra, não um token de API ou do MCP.',
    en: 'Only a person at the screen can set the origin of a rule, not an API or MCP token.',
  },
  [WorkflowOriginErrorCode.SOURCE_BILL_NOT_FOUND]: {
    ptBr: 'A conta de origem da regra não foi encontrada.',
    en: 'The source bill of the rule was not found.',
  },
};

export type WorkflowOriginInvalidReason =
  | 'not_an_object'
  | 'kind_unknown'
  | 'trigger_not_allowed'
  | 'description_trigger_not_allowed'
  | 'text_required'
  | 'text_too_long'
  | 'text_not_allowed'
  | 'source_bill_required'
  | 'source_bill_invalid';

export const WORKFLOW_ORIGIN_INVALID_REASON_MESSAGES: Record<
  WorkflowOriginInvalidReason,
  WorkflowOriginCopy
> = {
  not_an_object: {
    ptBr: 'A origem da regra deve ser um objeto com o tipo e os dados de origem.',
    en: 'The origin of the rule must be an object with its kind and source data.',
  },
  kind_unknown: {
    ptBr: 'O tipo de origem da regra não é conhecido.',
    en: 'The kind of origin is not known.',
  },
  trigger_not_allowed: {
    ptBr: 'Somente regras de conta a pagar (alteração de conta ou início da aprovação) podem ter origem.',
    en: 'Only bill rules (bill updated or approval start) can have an origin.',
  },
  description_trigger_not_allowed: {
    ptBr: 'Somente uma regra de alteração de conta pode ser criada a partir de uma descrição.',
    en: 'Only a bill updated rule can be created from a description.',
  },
  text_required: {
    ptBr: 'Uma regra criada a partir de uma descrição precisa do texto que a pessoa escreveu.',
    en: 'A rule created from a description needs the text the person wrote.',
  },
  text_too_long: {
    ptBr: `O texto da descrição pode ter no máximo ${WORKFLOW_ORIGIN_TEXT_MAX_LENGTH} caracteres.`,
    en: `The description can have at most ${WORKFLOW_ORIGIN_TEXT_MAX_LENGTH} characters.`,
  },
  text_not_allowed: {
    ptBr: 'Uma regra oferecida na aprovação não guarda texto: a conta de origem é a evidência.',
    en: 'A rule offered during approval keeps no text: the source bill is the evidence.',
  },
  source_bill_required: {
    ptBr: 'Uma regra oferecida na aprovação precisa da conta de origem.',
    en: 'A rule offered during approval needs its source bill.',
  },
  source_bill_invalid: {
    ptBr: 'A conta de origem deve ser um identificador válido.',
    en: 'The source bill must be a valid identifier.',
  },
};

export type WorkflowOriginCheck =
  | {
      ok: true;
      value: { kind: WorkflowOriginKind; text: string | null; sourceBillId: string | null };
    }
  | { ok: false; reason: WorkflowOriginInvalidReason };

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isOriginKind(value: unknown): value is WorkflowOriginKind {
  return (
    typeof value === 'string' && (Object.values(WorkflowOriginKind) as string[]).includes(value)
  );
}

/**
 * Checks what a caller sent as `origin` on create against the rules of the database CHECK, the
 * text limit and the allowed triggers. Returns the normalised value to store: the text trimmed,
 * absent fields as null, any key the caller invented dropped.
 */
export function checkWorkflowOriginInput(
  input: unknown,
  triggerTypeCode: string,
): WorkflowOriginCheck {
  if (input === null || typeof input !== 'object' || Array.isArray(input)) {
    return { ok: false, reason: 'not_an_object' };
  }
  const { kind, text, sourceBillId } = input as Record<string, unknown>;
  if (!isOriginKind(kind)) return { ok: false, reason: 'kind_unknown' };
  if (!WORKFLOW_ORIGIN_TRIGGER_CODES.includes(triggerTypeCode)) {
    return { ok: false, reason: 'trigger_not_allowed' };
  }

  const hasSourceBill = sourceBillId !== undefined && sourceBillId !== null && sourceBillId !== '';
  if (hasSourceBill && (typeof sourceBillId !== 'string' || !UUID_PATTERN.test(sourceBillId))) {
    return { ok: false, reason: 'source_bill_invalid' };
  }
  const sourceBill = hasSourceBill ? (sourceBillId as string) : null;

  if (kind === WorkflowOriginKind.APPROVAL_OFFER) {
    if (text !== undefined && text !== null) return { ok: false, reason: 'text_not_allowed' };
    if (sourceBill === null) return { ok: false, reason: 'source_bill_required' };
    return { ok: true, value: { kind, text: null, sourceBillId: sourceBill } };
  }

  if (!WORKFLOW_DESCRIPTION_ORIGIN_TRIGGER_CODES.includes(triggerTypeCode)) {
    return { ok: false, reason: 'description_trigger_not_allowed' };
  }
  if (typeof text !== 'string' || text.trim() === '') {
    return { ok: false, reason: 'text_required' };
  }
  const trimmed = text.trim();
  if (trimmed.length > WORKFLOW_ORIGIN_TEXT_MAX_LENGTH) {
    return { ok: false, reason: 'text_too_long' };
  }
  return { ok: true, value: { kind, text: trimmed, sourceBillId: sourceBill } };
}
